import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Crown, Users, Brain, TrendingUp, Plus, Key,
  Calendar, Shield, Eye, Settings, Copy, Play
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperUserDashboard = ({ user, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [inviteSettings, setInviteSettings] = useState({
    max_uses: 1,
    expires_hours: 72
  });
  const [newInviteCode, setNewInviteCode] = useState('');
  
  // Cinema Film Management
  const [showFilmUpload, setShowFilmUpload] = useState(false);
  const [films, setFilms] = useState([]);
  const [newFilm, setNewFilm] = useState({
    url: '',
    title: '',
    description: ''
  });
  const [uploadingFilm, setUploadingFilm] = useState(false);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  useEffect(() => {
    loadSuperDashboard();
  }, []);

  const loadSuperDashboard = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.get(`${API}/super/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      
      // Load cinema films
      loadFilms();
    } catch (error) {
      console.error('Super dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadFilms = async () => {
    try {
      const response = await axios.get(`${API}/cinema/all-films`);
      setFilms(response.data.films || []);
    } catch (error) {
      console.error('Error loading films:', error);
    }
  };
  
  const uploadNewFilm = async () => {
    if (!newFilm.url || !newFilm.title) {
      alert('Please provide both URL and title');
      return;
    }
    
    setUploadingFilm(true);
    try {
      const response = await axios.post(`${API}/cinema/upload-film`, {
        ...newFilm,
        user_email: user.email
      });
      
      if (response.data.success) {
        alert('🎬 Film uploaded successfully! This will now appear on the Cinema Experience page.');
        setNewFilm({ url: '', title: '', description: '' });
        setShowFilmUpload(false);
        loadFilms();
      }
    } catch (error) {
      console.error('Film upload error:', error);
      alert('Error uploading film: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingFilm(false);
    }
  };

  const createInviteCode = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.post(`${API}/super/create-invite`, inviteSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewInviteCode(response.data.invite_code);
      setShowCreateInvite(false);
      loadSuperDashboard(); // Refresh data
      
      // Copy to clipboard
      navigator.clipboard.writeText(response.data.invite_code);
      alert(`Invite code created: ${response.data.invite_code}\n(Copied to clipboard)`);
      
    } catch (error) {
      console.error('Invite creation error:', error);
      alert('Failed to create invite code');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    alert(`Invite code copied: ${code}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 to-blue-900">
        <div className="text-white text-xl">Loading super architecture...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral behind background */}
      <div className="fixed inset-0 pointer-events-none opacity-12" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(8px) saturate(2) brightness(1.6)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={500} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/80"></div>

      <div className="relative z-10 max-w-7xl mx-auto p-6">
        
        {/* Super User Header */}
        <div className="text-center mb-8 pt-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <Crown className="w-12 h-12 text-yellow-400 animate-pulse" />
            <h1 
              className="text-5xl font-bold text-white"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.5)'
              }}
            >
              Super Architect
            </h1>
            <Shield className="w-12 h-12 text-cyan-400 animate-pulse" />
          </div>
          <p 
            className="text-purple-200 text-xl"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Timeline Authority: {user.email}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Users className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.platform_analytics?.total_users || 0}
            </div>
            <p className="text-blue-200 text-sm">Total Travelers</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Brain className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.platform_analytics?.multiplayer_users || 0}
            </div>
            <p className="text-purple-200 text-sm">Multiplayer Access</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <TrendingUp className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.platform_analytics?.high_trust_users || 0}
            </div>
            <p className="text-green-200 text-sm">High Trust Users</p>
          </div>

          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
            <Key className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
            <div 
              className="text-3xl font-bold text-white"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {dashboardData?.invite_codes?.length || 0}
            </div>
            <p className="text-yellow-200 text-sm">Invite Codes</p>
          </div>
        </div>

        {/* 🎬 CINEMA FILM MANAGEMENT - Upload Film of the Day */}
        <div className="bg-gradient-to-br from-amber-900/40 to-black/60 backdrop-blur-xl border-2 border-amber-500/40 rounded-3xl p-8 shadow-2xl mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-amber-500/20 p-3 rounded-xl">
                <Play className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h2 
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  Cinema Film Management
                </h2>
                <p className="text-amber-200/80 text-sm">Upload Film of the Day for Cinema Experience</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowFilmUpload(!showFilmUpload)}
              className="bg-amber-600 hover:bg-amber-700 text-black font-semibold"
            >
              {showFilmUpload ? 'Cancel' : '+ Upload New Film'}
            </Button>
          </div>

          {/* Upload New Film Form */}
          {showFilmUpload && (
            <div className="bg-black/60 backdrop-blur-md border border-amber-500/30 rounded-2xl p-6 mb-6">
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                Upload New Film
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-amber-200 text-sm mb-2">Film URL (from Emergentagent or public URL)</label>
                  <input
                    type="text"
                    value={newFilm.url}
                    onChange={(e) => setNewFilm({...newFilm, url: e.target.value})}
                    placeholder="https://customer-assets.emergentagent.com/..."
                    className="w-full bg-black/60 border border-amber-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                  />
                </div>
                
                <div>
                  <label className="block text-amber-200 text-sm mb-2">Film Title</label>
                  <input
                    type="text"
                    value={newFilm.title}
                    onChange={(e) => setNewFilm({...newFilm, title: e.target.value})}
                    placeholder="e.g., Friday Night Jam, SpiralCred Moments"
                    className="w-full bg-black/60 border border-amber-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                  />
                </div>
                
                <div>
                  <label className="block text-amber-200 text-sm mb-2">Description (optional)</label>
                  <textarea
                    value={newFilm.description}
                    onChange={(e) => setNewFilm({...newFilm, description: e.target.value})}
                    placeholder="Brief description of the film..."
                    className="w-full bg-black/60 border border-amber-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-amber-500"
                    rows="2"
                  />
                </div>
                
                <Button
                  onClick={uploadNewFilm}
                  disabled={uploadingFilm || !newFilm.url || !newFilm.title}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-black font-bold py-3 disabled:opacity-50"
                >
                  {uploadingFilm ? '🎬 Uploading...' : '🎬 Upload & Set as Film of the Day'}
                </Button>
              </div>
            </div>
          )}

          {/* Current Films List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-amber-200 mb-3">Uploaded Films</h3>
            
            {films.length === 0 ? (
              <p className="text-white/60 text-center py-6">No films uploaded yet. Upload your first film above!</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {films.map((film, idx) => (
                  <div 
                    key={film.id}
                    className={`bg-black/40 backdrop-blur-sm border rounded-xl p-4 ${
                      film.is_active ? 'border-amber-500 bg-amber-900/20' : 'border-white/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-white font-semibold">{film.title}</h4>
                          {film.is_active && (
                            <Badge className="bg-amber-500 text-black">ACTIVE - Cinema</Badge>
                          )}
                        </div>
                        {film.description && (
                          <p className="text-white/70 text-sm mb-2">{film.description}</p>
                        )}
                        <p className="text-white/50 text-xs">
                          Uploaded: {new Date(film.uploaded_at).toLocaleDateString()} by {film.uploaded_by}
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(film.url);
                          alert('Film URL copied to clipboard!');
                        }}
                        variant="outline"
                        size="sm"
                        className="ml-4"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Invite Code Management */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Create Invite Codes */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-6">
              <Key className="w-8 h-8 text-yellow-400" />
              <h2 
                className="text-2xl font-bold text-white"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Invite Code Generation
              </h2>
            </div>
            
            {!showCreateInvite ? (
              <div className="text-center">
                <p 
                  className="text-white/80 text-lg mb-6"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Grant access to new consciousness travelers
                </p>
                
                <Button
                  onClick={() => setShowCreateInvite(true)}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-8 py-4 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Plus className="w-6 h-6 mr-3" />
                  Create Invite Code
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label 
                    className="block text-white text-lg mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Maximum Uses
                  </label>
                  <input
                    type="number"
                    value={inviteSettings.max_uses}
                    onChange={(e) => setInviteSettings({...inviteSettings, max_uses: parseInt(e.target.value)})}
                    className="w-full p-3 bg-white/10 border border-white/30 rounded-xl text-white"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    min="1"
                    max="100"
                  />
                </div>
                
                <div>
                  <label 
                    className="block text-white text-lg mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Expires In (Hours)
                  </label>
                  <input
                    type="number"
                    value={inviteSettings.expires_hours}
                    onChange={(e) => setInviteSettings({...inviteSettings, expires_hours: parseInt(e.target.value)})}
                    className="w-full p-3 bg-white/10 border border-white/30 rounded-xl text-white"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    min="1"
                    max="720"
                  />
                </div>
                
                <div className="flex space-x-4">
                  <Button
                    onClick={() => setShowCreateInvite(false)}
                    variant="outline"
                    className="text-white border-white/40 hover:bg-white/10"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    onClick={createInviteCode}
                    className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <Key className="w-5 h-5 mr-2" />
                    Generate Code
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Active Invite Codes */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
            <h2 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Active Invite Codes
            </h2>
            
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {dashboardData?.invite_codes?.filter(code => code.is_active).map((invite, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <code 
                      className="text-yellow-300 text-lg font-mono font-bold"
                      style={{ letterSpacing: '0.2em' }}
                    >
                      {invite.code}
                    </code>
                    
                    <button
                      onClick={() => copyInviteCode(invite.code)}
                      className="text-white/60 hover:text-white transition-colors"
                      title="Copy invite code"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="text-sm text-white/70 space-y-1">
                    <p>Uses: {invite.current_uses}/{invite.max_uses}</p>
                    <p>Created: {new Date(invite.created_at).toLocaleDateString()}</p>
                    {invite.expires_at && (
                      <p>Expires: {new Date(invite.expires_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              )) || (
                <div className="text-center text-white/60 py-8">
                  <Key className="w-12 h-12 mx-auto mb-4 opacity-40" />
                  <p 
                    className="italic"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    No active invite codes
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust Analytics */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl mb-8">
          <h2 
            className="text-2xl font-bold text-white mb-6"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Trust Analytics & Secret Journals
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* High Trust Users */}
            <div>
              <h3 
                className="text-white text-lg font-medium mb-4"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                High Trust Consciousness Travelers
              </h3>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {dashboardData?.high_trust_users?.map((user, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 border border-white/20">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={user.avatar_url}
                        alt=""
                        className="w-10 h-10 rounded-full border-2 border-white/30"
                      />
                      <div className="flex-1">
                        <p 
                          className="text-white font-medium"
                          style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                        >
                          {user.name}
                        </p>
                        <div className="flex items-center space-x-3 text-sm">
                          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500">
                            Trust: {user.trust_score}
                          </Badge>
                          <span className="text-white/60">{user.consciousness_level}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-xs text-white/60">
                      Journal entries: {user.secret_journal?.length || 0}
                    </div>
                  </div>
                )) || (
                  <div className="text-center text-white/60 py-8">
                    <p>No high trust users yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Trust Analytics */}
            <div>
              <h3 
                className="text-white text-lg font-medium mb-4"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Recent Trust Analytics
              </h3>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {dashboardData?.recent_trust_analytics?.map((analytics, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
                        <span className="text-white/80 text-sm">Trust Analysis</span>
                      </div>
                      <span className="text-white/60 text-xs">
                        {new Date(analytics.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-blue-300 font-medium">{analytics.authenticity_score || 0}</div>
                        <div className="text-white/60">Authenticity</div>
                      </div>
                      <div className="text-center">
                        <div className="text-green-300 font-medium">{analytics.collaboration_potential || 0}</div>
                        <div className="text-white/60">Collaboration</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-300 font-medium">{analytics.vision_alignment || 0}</div>
                        <div className="text-white/60">Vision</div>
                      </div>
                    </div>
                  </div>
                )) || (
                  <div className="text-center text-white/60 py-8">
                    <p>No trust analytics yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <Button
            onClick={onLogout}
            variant="outline"
            className="text-white border-white/40 hover:bg-white/10 px-8 py-3 text-lg"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Return to Timeline
          </Button>
        </div>
      </div>

      {/* Create Invite Modal */}
      {showCreateInvite && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 
              className="text-white text-2xl font-bold mb-6 text-center"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Create Invite Code
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-white text-lg mb-3">Maximum Uses</label>
                <input
                  type="number"
                  value={inviteSettings.max_uses}
                  onChange={(e) => setInviteSettings({...inviteSettings, max_uses: parseInt(e.target.value)})}
                  className="w-full p-3 bg-white/10 border border-white/30 rounded-xl text-white"
                  min="1"
                  max="100"
                />
              </div>
              
              <div>
                <label className="block text-white text-lg mb-3">Expires In (Hours)</label>
                <input
                  type="number"
                  value={inviteSettings.expires_hours}
                  onChange={(e) => setInviteSettings({...inviteSettings, expires_hours: parseInt(e.target.value)})}
                  className="w-full p-3 bg-white/10 border border-white/30 rounded-xl text-white"
                  min="1"
                  max="720"
                />
              </div>
              
              <div className="flex space-x-4">
                <Button
                  onClick={() => setShowCreateInvite(false)}
                  variant="outline"
                  className="flex-1 text-white border-white/40"
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={createInviteCode}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600"
                >
                  Generate
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperUserDashboard;